import React, { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { Chip, Tooltip, Typography } from "@mui/material";


function convertTo12HourFormat(timeString) {
  if (!timeString) return "N/A"; 
  try {
    let cleanTime = timeString;
    if (timeString.includes("T")) {
      cleanTime = timeString.split("T")[1].split(".")[0];
    }
    let [hours, minutes] = cleanTime.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return timeString; 
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  } catch (err) {
    return timeString;
  }
}

const RealTimeBookingDataGrid = ({
  franchiseId,
  websocketUrl,
  setRealTimeBookingsCount,
  tokenKey,
}) => {
  const [realTimeBookings, setRealTimeBookings] = useState([]);
  const [websocket, setWebsocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("Connecting...");

  // --- WebSocket Connection Logic ---
  useEffect(() => {
    const token = localStorage.getItem(tokenKey);

    if (!token) {
      console.error(" Authentication Token is missing.");
      setConnectionStatus("Authentication Error");
      return;
    }

    const wsUrl = websocketUrl.endsWith("/")
      ? websocketUrl
      : `${websocketUrl}/`;
    const wsUrlWithToken = `${wsUrl}?token=${token}`;

    console.log(" Connecting to WebSocket:", wsUrlWithToken);
    console.log(" Franchise ID:", franchiseId);
    console.log(" Token available:", !!token);

    const ws = new WebSocket(wsUrlWithToken);

    ws.onopen = () => {
      console.log(" WebSocket Connected successfully");
      //   console.log(" WebSocket ready state:", ws.readyState);
      console.log(" Listening for bookings");
      setConnectionStatus("Connected");

      console.log(" Waiting for backend messages...");
      console.log(" Backend should automatically broadcast booking events");

    };

    ws.onmessage = (event) => {
      console.log(" Raw WebSocket message received:", event.data);
      console.log(" Message received at:", new Date().toLocaleTimeString());

      try {
        const data = JSON.parse(event.data);
        console.log(" Parsed WebSocket data:", data);
        console.log(" Message type:", data.type);
        console.log(" Full data structure:", JSON.stringify(data, null, 2));

        // Check if this is a booking-related message
        if (
          data.type === "booking_update" ||
          data.type === "new_booking" ||
          data.type === "booking_status_updated" ||
          data.type === "booking_created"
        ) {
          // Added this type just in case

          console.log(" Booking message detected!");

          // Try multiple possible data structures
          let newBookingData = null;

          // Option 1: data.data exists
          if (data.data && typeof data.data === "object") {
            newBookingData = data.data;
            console.log("✓ Found booking in data.data:", newBookingData);
          }
          // Option 2: data.booking exists
          else if (data.booking && typeof data.booking === "object") {
            newBookingData = data.booking;
            console.log("✓ Found booking in data.booking:", newBookingData);
          }
          // Option 3: data itself is the booking (no nested structure)
          else if (data.id) {
            newBookingData = data;
            console.log("✓ Found booking in root data:", newBookingData);
          }

          if (!newBookingData || !newBookingData.id) {
            console.error(
              " Received message without valid booking data. Full data:",
              data
            );
            console.log(" Available keys in data:", Object.keys(data));
            return;
          }

          console.log(" Valid booking data found with ID:", newBookingData.id);
          console.log(" Booking details:", {
            id: newBookingData.id,
            service: newBookingData.service || newBookingData.service_name,
            customer: newBookingData.name || newBookingData.customer_name,
            status: newBookingData.status,
            business_id:
              newBookingData.business_id || newBookingData.franchise_id,
          });

          // Optional: Filter by franchise ID (uncomment if needed for specific franchise)
          // if (franchiseId && newBookingData.business_id && newBookingData.business_id !== franchiseId) {
          //   console.log(` Skipping booking - different franchise (${newBookingData.business_id} !== ${franchiseId})`);
          //   return;
          // }

          // Add or Update the booking in the realTimeBookings state
          setRealTimeBookings((prevBookings) => {
            console.log(" Current bookings count:", prevBookings.length);

            const existingIndex = prevBookings.findIndex(
              (b) => b.id === newBookingData.id
            );
            let updatedBookings;

            if (existingIndex !== -1) {
              console.log(
                " Updating existing booking at index:",
                existingIndex
              );
              // Update existing booking
              updatedBookings = prevBookings.map((booking, index) =>
                index === existingIndex ? newBookingData : booking
              );
            } else {
              console.log(" Adding new booking to the top");
              // Add new booking at the top
              updatedBookings = [newBookingData, ...prevBookings];
            }

            // Limit the number of displayed real-time bookings (e.g., to 25)
            updatedBookings = updatedBookings.slice(0, 25);

            console.log(" Updated bookings count:", updatedBookings.length);
            setRealTimeBookingsCount(updatedBookings.length);

            return updatedBookings;
          });
        } else {
          console.log(" Received non-booking message type:", data.type);
          console.log(" Full message:", data);
        }
      } catch (error) {
        console.error(" Error parsing WebSocket message:", error);
        console.log(" Raw message that failed:", event.data);
      }
    };

    ws.onerror = (error) => {
      console.error(" WebSocket Error:", error);
      setConnectionStatus("Error");
    };

    ws.onclose = (event) => {
      console.log(" WebSocket Disconnected");
      console.log("Close code:", event.code);
      console.log("Close reason:", event.reason);
      setConnectionStatus("Disconnected");
    };

    setWebsocket(ws);

    // Clean up the WebSocket connection when the component unmounts
    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        console.log(" Cleaning up WebSocket connection");
        ws.close();
      }
    };
  }, [franchiseId, websocketUrl, setRealTimeBookingsCount, tokenKey]);

  // --- Data Grid Logic ---

  const rows =
    realTimeBookings?.map((booking) => {
      console.log(" Processing booking for DataGrid:", booking);

      return {
        id: booking.id,
        service_name: booking.service_name || booking.service,
        phone: booking.phone,
        customer_name: booking.customer_name || booking.name,
        customer_address: booking.customer_address || booking.address,
        // Handle both date formats: "14-11-2025" or "2025-11-14"
        date: booking.date || booking.booked_date,
        worker_name: booking.worker_name,
        // Handle time - could be "09:55:23" or "09:55:00"
        time: convertTo12HourFormat(booking.time || booking.booked_time),
        status: booking.status,
        booking_price: booking.booking_price,
        miscellaneous_price: booking.miscellaneous_price,
        payment_method: booking.payment_method,
        payable_amount: booking.payable_amount,
        service_charge: booking.service_charge,
        is_booked: booking.is_booked,
        is_paid: booking.is_paid,
        latitude: booking.latitude,
        longitude: booking.longitude,
        total_price: booking.total_price,
        miscellaneous_price_comment: booking.miscellaneous_price_comment,
        service_charge_comment: booking.service_charge_comment,
        booked_method: booking.booked_method,
        rating: booking.rating,
      };
    }) || [];

  const bookingWithIndex = rows.map((booking, index) => {
    const indexed = {
      ...booking,
      displayIndex: index + 1,
    };
    console.log(` Row ${index + 1}:`, indexed);
    return indexed;
  });

  const columns = useMemo(
    () => [
      { field: "displayIndex", headerName: "No", width: 50 },
      { field: "id", headerName: "ID", width: 70 },
      { field: "service_name", headerName: "Service Name", width: 150 },
      { field: "customer_name", headerName: "Customer Name", width: 140 },
      { field:"phone",headerName:"Customer Number", width:135},
      {field:"customer_address",headerName:"Customer Address",width:140},
      { field: "date", headerName: "Date", width: 100 },
      { field: "time", headerName: "Time", width: 100 },
      { field: "worker_name", headerName: "Worker", width: 120 },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => {
          let chipColor;

          switch (params.value) {
            case "Waiting":
              chipColor = "warning";
              break;
            case "Accepted":
              chipColor = "success";
              break;
            case "Cancelled":
              chipColor = "error";
              break;
            case "Estimated":
              chipColor = "primary";
              break;
            case "Completed":
              chipColor = "secondary";
              break;
            default:
              chipColor = "default";
          }

          return (
            <Chip
              label={params.value}
              color={chipColor}
              size="small"
              style={{
                color: "white",
                minWidth: "80px",
              }}
            />
          );
        },
      },
      {
        field: "is_paid",
        headerName: "Payment",
        width: 100,
        renderCell: (params) => (
          <span
            style={{
              color: params.value ? "green" : "red",
              fontWeight: "bold",
              fontSize: "12px",
            }}
          >
            {params.value ? "Paid" : "Pending"}
          </span>
        ),
      },
      {
        field: "is_booked",
        headerName: "Booking",
        width: 100,
        renderCell: (params) => {
          const isBooked = params.value;
          const tooltipTitle = isBooked
            ? params.row.booked_method || "N/A"
            : "";

          return (
            <Tooltip
              title={tooltipTitle}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#333",
                    color: "#fff",
                    fontSize: "12px",
                    padding: "4px",
                    borderRadius: "4px",
                  },
                },
              }}
            >
              <span
                style={{
                  color: isBooked ? "green" : "red",
                  fontWeight: "bold",
                  fontSize: "12px",
                  cursor: isBooked ? "pointer" : "default",
                }}
              >
                {isBooked ? "₹100 Taken" : "-"}
              </span>
            </Tooltip>
          );
        },
      },
      {field:"booking_price",headerName:"Booking Price",width:130},

    ],
    []
  );

  return (
    <div className="mt-3">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <Typography variant="h6" component="h2">
          New Bookings ({realTimeBookings.length})
        </Typography>
        <span
          style={{
            fontSize: "12px",
            padding: "4px 8px",
            borderRadius: "4px",
            backgroundColor:
              connectionStatus === "Connected" ? "#e8f5e9" : "#ffebee",
            color: connectionStatus === "Connected" ? "#2e7d32" : "#c62828",
            fontWeight: "bold",
          }}
        >
          {connectionStatus === "Connected" ? "🟢" : "🔴"} {connectionStatus}
        </span>
      </div>

      <Box
        sx={{
          flexGrow: 1,
          //   maxWidth: { xs: 650, sm: 682, lg: 1000 },
          bgcolor: "background.paper",
          border: "1px solid #ccc",
          borderRadius: "4px",
          boxShadow: "0 0px 15px rgba(115, 230, 124,1)",
          marginBottom: "20px",
        }}
      >
        {bookingWithIndex.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
              fontStyle: "italic",
            }}
          >
            Waiting for new bookings...
          </div>
        ) : (
          <DataGrid
            rows={bookingWithIndex}
            columns={columns}
            pageSizeOptions={[10, 25]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              sorting: {
                sortModel: [{ field: "id", sort: "desc" }],
              },
            }}
            disableSelectionOnClick
          />
        )}
      </Box>
    </div>
  );
};

export default RealTimeBookingDataGrid;
